// iter690 — POS Terminal branch picker dialog.
//
// Admin at a branch opens this on their POS device (iPad / PC) and
// picks "this terminal lives at Branch X". The picked branch_id is
// written to localStorage and read by CashierLoginPage on every PIN
// login, then forwarded to the backend in the login body. The server
// then stamps the cashier's session with the TERMINAL's branch — not
// the cashier's "home" branch on the employee roster — so every sale
// rung up on this device is correctly attributed.
//
// Why localStorage and not the auth profile?
//   • A SINGLE physical device may host different cashiers across the
//     day. The terminal's identity is the constant; the cashier rotates.
//   • Avoids round-trips to a per-device server record (no extra DB
//     table, no migrations).
//   • Survives page reloads + tab close; cleared when admin clicks
//     "Use cashier's home branch (default)".

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Building2, X } from 'lucide-react';
import { toast } from 'sonner';
import { sendPosHeartbeat } from '@/lib/deviceHeartbeat';
import api from '@/lib/api';
import { useBranchStore } from '@/stores/branchStore';

const STORAGE_KEY = 'pos_terminal_branch_id';

export default function PosTerminalBranchDialog({ open, onOpenChange, branches }) {
  const [current, setCurrent] = useState('');
  const [picked, setPicked] = useState('');
  // iter-fix — keep the shared acting-branch store in lockstep with the pin.
  const setActiveBranchId = useBranchStore((s) => s.setActiveBranchId);

  useEffect(() => {
    if (!open) return;
    let val = '';
    try { val = localStorage.getItem(STORAGE_KEY) || ''; } catch { /* ignore */ }
    setCurrent(val);
    setPicked(val);
  }, [open]);

  // Is a cashier mid-shift on THIS device? Block branch reassignment so open
  // shifts / unfinished payments / pending orders are never re-attributed.
  const hasOpenShift = async () => {
    try {
      const token = localStorage.getItem('cashier_token');
      if (!token) return false;
      const { data } = await api.get('/cashier/shift/current', { headers: { Authorization: `Bearer ${token}` } });
      return !!(data && data.status !== 'closed');
    } catch { return false; }
  };

  // Single path for both Save and Clear so the terminal pin AND the shared
  // active-branch store always move together, and a changed assignment forces a
  // fresh, backend-validated cashier session (prevents a stale session or a
  // stale pos_active_branch_id keeping the OLD branch — the reported bug).
  const applyAssignment = async (next) => {
    const nextId = next || '';
    const changed = nextId !== (current || '');
    if (changed && await hasOpenShift()) {
      toast.error('Close the active shift (and clear any held / unpaid orders) before changing this terminal\'s branch.');
      return;
    }
    try {
      if (nextId) localStorage.setItem(STORAGE_KEY, nextId);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore localStorage errors (private browsing, etc.) */ }
    // Keep the shared acting-branch (POSBranchPicker / Stock / Reports) in sync.
    setActiveBranchId(nextId || null);
    // Best-effort sync of the terminal→branch assignment to the pos_devices record.
    sendPosHeartbeat();
    if (changed) {
      // Force a fresh PIN login so the backend re-stamps + re-validates the
      // branch against the cashier's allowed branches on the new terminal.
      try { localStorage.removeItem('cashier_token'); localStorage.removeItem('cashier_user'); } catch { /* ignore */ }
    }
    const name = branches.find(b => b.id === nextId)?.name;
    if (nextId) {
      toast.success(`This device is now pinned to ${name || nextId}.${changed ? ' Cashier must log in again to continue.' : ''}`);
    } else {
      toast.success(`Terminal pin cleared — cashier login will use their home branch.${changed ? ' Cashier must log in again.' : ''}`);
    }
    setCurrent(nextId);
    onOpenChange(false);
  };

  const save = () => applyAssignment(picked);

  const clear = () => { setPicked(''); applyAssignment(''); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pos-terminal-branch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 size={18} className="text-emerald-600" />
            This device&apos;s branch
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          <p className="text-[12px] text-stone-700 leading-snug">
            Pin THIS physical iPad / PC to a branch. Every cashier who logs in here today &amp; every sale they ring up will be recorded under THIS branch, even if their home branch is somewhere else.
          </p>

          {current && (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] flex items-center gap-2">
              <CheckCircle2 size={13} className="text-emerald-700 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-emerald-900">Currently pinned to</div>
                <div className="text-emerald-800 font-mono text-[10px] truncate">{branches.find(b => b.id === current)?.name || current}</div>
              </div>
              <button
                type="button"
                onClick={clear}
                className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-emerald-300 hover:bg-emerald-50 text-emerald-800"
                data-testid="pos-terminal-branch-clear-btn"
              >
                Clear
              </button>
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Pick a branch</div>
            <div className="max-h-72 overflow-y-auto space-y-1 -mx-1 px-1">
              <button
                type="button"
                onClick={() => setPicked('')}
                className={`w-full text-left px-2.5 py-2 rounded-md border text-[12px] transition ${
                  picked === '' ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200' : 'border-zinc-200 bg-white hover:bg-zinc-50'
                }`}
                data-testid="pos-terminal-branch-option-none"
              >
                <div className="font-semibold text-stone-700 flex items-center gap-1">
                  <X size={11} /> No pin (use cashier&apos;s home branch)
                </div>
                <div className="text-[10px] text-muted-foreground">Default — cashier&apos;s home branch is used.</div>
              </button>
              {(branches || []).map(b => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setPicked(b.id)}
                  className={`w-full text-left px-2.5 py-2 rounded-md border text-[12px] transition ${
                    picked === b.id ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200' : 'border-zinc-200 bg-white hover:bg-zinc-50'
                  }`}
                  data-testid={`pos-terminal-branch-option-${b.id}`}
                >
                  <div className="font-semibold text-stone-800 truncate">{b.name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono truncate">{b.id}</div>
                </button>
              ))}
              {(!branches || branches.length === 0) && (
                <div className="text-[11px] italic text-muted-foreground py-3 text-center">No branches yet — add one in Settings → Branches.</div>
              )}
            </div>
          </div>

          <p className="text-[10px] text-muted-foreground leading-snug border-t border-zinc-200 pt-2">
            Tip: clear this when a device is permanently moved. The next admin who logs in on this device sees the same dialog under Settings → Printers → Setup → &quot;This device&apos;s branch&quot;.
          </p>
        </div>

        <DialogFooter className="pt-3">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="pos-terminal-branch-cancel">Cancel</Button>
          <Button
            size="sm"
            onClick={save}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="pos-terminal-branch-save"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
